import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  Info, 
  AlertCircle, 
  XCircle,
  Clock,
  Cpu,
  Radio,
  Calendar,
  CheckCircle2,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ApiAlarm, AlarmSeverity, AlarmType } from '@/types/api';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface AlarmCardProps {
  alarm: ApiAlarm;
  onViewDetails: (alarm: ApiAlarm) => void;
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
}

// Iconos por tipo de alarma
const getAlarmIcon = (type: AlarmType) => {
  const iconMap: Record<string, React.ReactNode> = {
    irrigation_missed: <Calendar className="w-4 h-4" />,
    irrigation_out_of_hours: <Clock className="w-4 h-4" />,
    supervision_started: <CheckCircle2 className="w-4 h-4" />,
    supervision_stopped: <X className="w-4 h-4" />,
    time_sync_failed: <AlertCircle className="w-4 h-4" />,
    time_sync_corrected: <CheckCircle2 className="w-4 h-4" />,
    device_offline: <XCircle className="w-4 h-4" />,
    device_back_online: <CheckCircle2 className="w-4 h-4" />,
    schedule_sync_failed: <AlertCircle className="w-4 h-4" />,
    schedule_conflict: <AlertTriangle className="w-4 h-4" />,
    schedule_mismatch: <AlertTriangle className="w-4 h-4" />,
    schedule_missing_device: <XCircle className="w-4 h-4" />,
    schedule_extra_device: <AlertTriangle className="w-4 h-4" />,
    schedule_verification_err: <AlertCircle className="w-4 h-4" />,
  };
  return iconMap[type] || <Info className="w-4 h-4" />;
};

// Colores por severidad
const getSeverityColors = (severity: AlarmSeverity) => {
  const colors = {
    info: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    warning: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    alarm: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
    critical: 'bg-red-500/10 text-red-600 border-red-500/20',
  };
  return colors[severity];
};

// Colores de estado
const getStatusColors = (status: string) => {
  const colors = {
    active: 'bg-red-500/10 text-red-600 border-red-500/20',
    acknowledged: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    resolved: 'bg-green-500/10 text-green-600 border-green-500/20',
  };
  return colors[status as keyof typeof colors] || colors.active;
};

export function AlarmCard({ alarm, onViewDetails, onAcknowledge, onResolve }: AlarmCardProps) {
  const isActive = alarm.status === 'active';
  const isAcknowledged = alarm.status === 'acknowledged';
  const isResolved = alarm.status === 'resolved';

  // Formatear fecha
  const timeAgo = formatDistanceToNow(new Date(alarm.createdAt), {
    addSuffix: true,
    locale: es,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Card 
        className={cn(
          'group hover:shadow-card-hover transition-all duration-300 hover:-translate-y-0.5 cursor-pointer',
          isActive && 'border-l-4 border-l-red-500',
          isAcknowledged && 'border-l-4 border-l-yellow-500',
          isResolved && 'border-l-4 border-l-green-500 opacity-75'
        )}
        onClick={() => onViewDetails(alarm)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className={cn(
                'p-2 rounded-lg',
                getSeverityColors(alarm.severity)
              )}>
                {getAlarmIcon(alarm.type)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                  {alarm.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {alarm.message}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge className={cn('text-xs', getSeverityColors(alarm.severity))}>
                {alarm.severityLabel}
              </Badge>
              <Badge variant="outline" className={cn('text-xs', getStatusColors(alarm.status))}>
                {alarm.status === 'active' ? 'Activa' : alarm.status === 'acknowledged' ? 'Reconocida' : 'Resuelta'}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Información del dispositivo */}
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {alarm.controllerName && (
              <div className="flex items-center gap-1">
                <Cpu className="w-3 h-3" />
                <span>{alarm.controllerName}</span>
              </div>
            )}
            {alarm.sensorName && (
              <div className="flex items-center gap-1">
                <Radio className="w-3 h-3" />
                <span>{alarm.sensorName}</span>
              </div>
            )}
          </div>

          {/* Tipo de alarma */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {alarm.typeLabel}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {timeAgo}
            </span>
          </div>

          {/* Acciones */}
          {isActive && (
            <div className="flex gap-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => onAcknowledge(alarm.id)}
              >
                Reconocer
              </Button>
              <Button
                variant="default"
                size="sm"
                className="flex-1"
                onClick={() => onResolve(alarm.id)}
              >
                Resolver
              </Button>
            </div>
          )}
          {isAcknowledged && (
            <div className="flex gap-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="default"
                size="sm"
                className="flex-1"
                onClick={() => onResolve(alarm.id)}
              >
                Resolver
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
